import { useCallback, useState } from 'react';

import { storageKeys } from 'config/settings.json';


import APIHandler from 'config/APIHandler';
import StorageHandler from 'config/StorageHandler';
import i18n from 'i18n-react';
import { toast } from 'react-toastify';

interface UploadResponse {
  path: string;
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onabort = () => {
      reject(new Error('file reading was aborted'));
    };

    reader.onerror = () => {
      reject(new Error('file reading has failed'));
    };

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.readAsDataURL(file);
  });
}

export function useFileUpload() {
  const [uploading, setUploading] = useState(false);

  const uploadFile = useCallback(async (file: File): Promise<string> => {
    // max 100mb
    if (file.size > 100000000) {
      toast.error(i18n.translate('Sorry, max File Size is 100mb'), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw new Error('File too large');
    }

    setUploading(true);
    try {
      const binaryStr = await readAsDataURL(file);

      const data = {
        name: file.name,
        type: file.type,
        size: file.size,
        url: binaryStr,
      };

      const token = (await StorageHandler.load(storageKeys.accessToken)) as string;
      const response = (await APIHandler.uploadFile(data, token)) as UploadResponse;

      return response.path;
    } catch (error) {
      console.error(error);
      toast.error(i18n.translate('File upload failed'));
      throw error;
    } finally {
      setUploading(false);
    }
  }, []);

  return { uploadFile, uploading };
}
